import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import AmbientBackground from '../common/AmbientBackground';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../config/supabase';

interface Profile {
  display_name: string;
}

const PillLink: React.FC<{ to: string; label: string; delay?: number }> = ({ to, label, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.4, ease: 'easeOut' }}
  >
    <Link
      to={to}
      className="inline-flex items-center gap-3 rounded-full bg-moon dark:bg-moon/90 px-6 py-3 hover:-translate-y-px hover:bg-dusk dark:hover:bg-dusk transition-all group"
    >
      <span className="w-8 h-8 rounded-full bg-sun dark:bg-ember flex items-center justify-center text-white group-hover:bg-ember dark:group-hover:bg-sun transition-colors">
        <ArrowRight size={16} />
      </span>
      <span className="font-display text-lg text-parchment">{label}</span>
    </Link>
  </motion.div>
);

const HomeHero: React.FC = () => {
  const { user } = useAuthStore();
  const [profile, setProfile] = React.useState<Profile | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('display_name')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        setProfile(data);
      } catch (err) {
        console.error('Error fetching profile:', err);
      }
    };

    fetchProfile();
  }, [user]);

  return (
    <AmbientBackground
      className="min-h-[calc(100vh-8rem)]"
      backgroundImage="/bg-hero.png"
      backgroundVideo="/bg-hero-video.mp4"
    >
      {/* Full-viewport decorative frame */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="min-h-[calc(100vh-8rem)] m-4 md:m-6 border-[6px] border-parchment dark:border-parchment/20 rounded-2xl flex flex-col items-center justify-center px-6 py-12"
      >
        {/* Title */}
        <motion.h1
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="text-6xl md:text-7xl lg:text-8xl font-display font-semibold text-ink dark:text-parchment text-center"
          style={{ textShadow: '0 1px 12px rgba(245,240,232,0.8)' }}
        >
          Tara's Tarot
        </motion.h1>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="mt-4 text-sm tracking-[0.08em] uppercase text-ink/70 dark:text-mist text-center"
          style={{ textShadow: '0 1px 8px rgba(245,240,232,0.6)' }}
        >
          Your tarot practice. Deepened.
        </motion.p>

        {user ? (
          /* ── Logged-in view ── */
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="mt-12 w-full max-w-3xl space-y-10 text-center"
          >
            <p className="text-ink/80 dark:text-parchment/60" style={{ textShadow: '0 1px 8px rgba(245,240,232,0.6)' }}>
              Hi {profile?.display_name}, ready to pick up where you left off?
            </p>

            <div className="flex flex-col md:flex-row items-center justify-center gap-4">
              <PillLink to="/new-reading" label="New Reading" delay={0.7} />
              <PillLink to="/history" label="Your Journal" delay={0.8} />
              <PillLink to="/virtual-deck" label="Virtual Deck" delay={0.9} />
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.0, duration: 0.4 }}
              className="text-sm text-ink/70 dark:text-parchment/50"
              style={{ textShadow: '0 1px 8px rgba(245,240,232,0.6)' }}
            >
              Or head to the{' '}
              <Link to="/guide" className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember underline underline-offset-2">
                Tarot Guide
              </Link>{' '}
              for card meanings, spreads, and more.
            </motion.p>

            <div className="text-center space-x-4">
              <Link
                to="/guide/101"
                className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
              >
                Tarot 101
              </Link>
              <span className="text-mist">•</span>
              <Link
                to="/about"
                className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
              >
                About Tara
              </Link>
              <span className="text-mist">•</span>
              <Link
                to="/faq"
                className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
              >
                About This App
              </Link>
            </div>
          </motion.div>
        ) : (
          /* ── Logged-out view ── */
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="mt-16 w-full max-w-3xl space-y-12 text-center"
          >
            {/* Deck choice */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.4 }}
              className="font-display italic text-xl text-ink dark:text-parchment"
              style={{ textShadow: '0 1px 10px rgba(245,240,232,0.7)' }}
            >
              Do you have a physical deck?
            </motion.p>

            <div className="flex flex-col md:flex-row items-center justify-center gap-4">
              <PillLink to="/start" label="Yes, I Have a Deck" delay={0.7} />
              <PillLink to="/virtual-deck" label="No, Go Virtual" delay={0.8} />
            </div>

            {/* Log in / Sign up — quiet text links */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.0, duration: 0.4 }}
              className="space-y-2"
            >
              <p className="text-sm text-ink/70 dark:text-mist" style={{ textShadow: '0 1px 8px rgba(245,240,232,0.6)' }}>
                Want to keep track of your tarot journey?
              </p>
              <div className="flex justify-center gap-4">
                <Link
                  to="/login"
                  className="text-sm text-ink/60 dark:text-mist hover:text-sun dark:hover:text-ember transition-colors"
                >
                  Log In
                </Link>
                <span className="text-mist">·</span>
                <Link
                  to="/signup"
                  className="text-sm text-ink/60 dark:text-mist hover:text-sun dark:hover:text-ember transition-colors"
                >
                  Sign Up
                </Link>
              </div>
            </motion.div>

            {/* Bottom links */}
            <div className="text-center space-x-4">
              <Link
                to="/guide/101"
                className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
              >
                Tarot 101
              </Link>
              <span className="text-mist">•</span>
              <Link
                to="/about"
                className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
              >
                About Tara
              </Link>
              <span className="text-mist">•</span>
              <Link
                to="/faq"
                className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
              >
                About This App
              </Link>
            </div>
          </motion.div>
        )}
      </motion.div>
    </AmbientBackground>
  );
};

export default HomeHero;
